/**
 * AIP Score Calculator Utility - NEW METHODOLOGY v2.0 (frontend copy)
 *
 * Mirrors backend utils/aip-calculator-v2.js for client-side, real-time scoring.
 */

import {
  FWB_ADL_ITEMS,
  scoreCareDaysPerWeek,
  countChecked,
  scoreFallHazardsFromCount,
  scoreBathroomFromHazardCount,
  scoreKitchenFromHazardCount,
  scoreHomeLayoutFromBarrierCount,
  resolveGripStrengthScore,
} from "./aipScoringRubrics";

export const calculateScoresV2 = (data, config = {}) => {
  const lambda = config.lambda ?? 0.3; // Philosophy control (0.3 = balanced)
  const gamma = config.gamma ?? 0.5; // Environmental influence (0.5 = moderate)
  const sigma = config.sigma ?? 0.8; // Substitution quality factor (0.8)

  const getNum = (val) => {
    const n = parseFloat(val);
    if (Number.isNaN(n)) return 0;
    return n;
  };
  const getIndependenceScore = (status) => {
    if (typeof status === "number") {
      return Math.max(0, Math.min(2, status));
    }
    if (!status || status === "not-assessed" || status === "") return 0;
    const lower = String(status).toLowerCase();
    if (lower.includes("indep")) return 2;
    if (lower.includes("assist")) return 1;
    if (lower.includes("depend")) return 0;
    return 0;
  };

  const migrateData = (raw) => {
    const migrated = { ...(raw || {}) };
    if (raw?.adlFeeding !== undefined && migrated.adlEating === undefined) {
      migrated.adlEating = raw.adlFeeding;
    }
    if (raw?.adlMobility !== undefined && migrated.adlTransferring === undefined) {
      migrated.adlTransferring = raw.adlMobility;
    }
    if (raw?.adlRestroom !== undefined && migrated.adlToileting === undefined) {
      migrated.adlToileting = raw.adlRestroom;
    }
    if (
      raw?.iadlMealPreparation !== undefined &&
      migrated.iadlMeals === undefined
    ) {
      migrated.iadlMeals = raw.iadlMealPreparation;
    }
    if (
      raw?.iadlHousekeepingLaundry !== undefined &&
      migrated.iadlHousekeeping === undefined
    ) {
      migrated.iadlHousekeeping = raw.iadlHousekeepingLaundry;
    }
    if (
      raw?.iadlHomemaintenance !== undefined &&
      migrated.iadlHomeMaintenance === undefined
    ) {
      migrated.iadlHomeMaintenance = raw.iadlHomemaintenance;
    }
    if (
      raw?.iadlTelephone !== undefined &&
      migrated.iadlCommunication === undefined
    ) {
      migrated.iadlCommunication = raw.iadlTelephone;
    }
    if (raw?.careDaysPerWeek !== undefined) {
      const current = getNum(migrated.ctDaysPerWeek);
      if (!Number.isFinite(current) || current === 0) {
        migrated.ctDaysPerWeek = raw.careDaysPerWeek;
      }
    }
    if (
      raw?.totalScheduledVisits !== undefined &&
      raw?.visitsOnTime !== undefined
    ) {
      const current = getNum(migrated.ctReliabilityPercent);
      if (!Number.isFinite(current) || current === 0) {
        const scheduled = getNum(raw.totalScheduledVisits) || 0;
        const onTime = getNum(raw.visitsOnTime) || 0;
        migrated.ctReliabilityPercent =
          scheduled > 0 ? (onTime / scheduled) * 100 : 0;
      }
    }
    if (
      raw?.totalVisits !== undefined &&
      raw?.visitsBySamePerson !== undefined
    ) {
      const current = getNum(migrated.ctContinuityPercent);
      if (!Number.isFinite(current) || current === 0) {
        const totalVisits = getNum(raw.totalVisits) || 0;
        const samePerson = getNum(raw.visitsBySamePerson) || 0;
        migrated.ctContinuityPercent =
          totalVisits > 0 ? (samePerson / totalVisits) * 100 : 0;
      }
    }
    if (typeof raw?.carePlanStatus === "string") {
      if (migrated.ctCareplanCurrent !== true) {
        migrated.ctCareplanCurrent =
          raw.carePlanStatus.toLowerCase() === "up to date";
      }
    }
    if (raw?.familySatisfaction !== undefined) {
      const current = getNum(migrated.ctFamilyCommunication);
      if (!Number.isFinite(current) || current === 0 || current === 50) {
        const satisfaction = getNum(raw.familySatisfaction);
        migrated.ctFamilyCommunication =
          satisfaction > 5 ? satisfaction : ((satisfaction - 1) / 4) * 100;
      }
    }
    const mapGripLabel = (val) => {
      if (val === undefined || val === null || val === "") return null;
      const lower = String(val).toLowerCase();
      if (lower === "firm") return 4;
      if (lower === "weak") return 2;
      const n = getNum(val);
      return n > 0 ? n : null;
    };
    if (migrated.gripRight == null || migrated.gripRight === 0) {
      const mapped = mapGripLabel(raw?.rightGrip);
      if (mapped != null) migrated.gripRight = mapped;
    }
    if (migrated.gripLeft == null || migrated.gripLeft === 0) {
      const mapped = mapGripLabel(raw?.leftGrip);
      if (mapped != null) migrated.gripLeft = mapped;
    }
    return migrated;
  };

  data = migrateData(data);

  const meaningfulFields = [
    "adlBathing",
    "adlDressing",
    "adlToileting",
    "adlTransferring",
    "adlContinence",
    "adlEating",
    "iadlFinances",
    "iadlTransportation",
    "iadlShopping",
    "iadlMeals",
    "iadlCommunication",
    "iadlMedication",
    "iadlHousekeeping",
    "iadlHomeMaintenance",
    "gaitSpeed",
    "mobilityDevice",
    "gripRight",
    "gripLeft",
    "fallsLastYear",
    "ctDaysPerWeek",
    "ctReliabilityPercent",
    "ctContinuityPercent",
  ];

  const hasData = meaningfulFields.some((field) => {
    const val = data[field];
    return (
      val !== undefined && val !== null && val !== "" && val !== "not-assessed"
    );
  });

  if (!hasData) {
    return {
      aipScore: 0,
      wellBeingScore: 0,
      careTeamScore: 0,
      environmentScore: 100, // default when nothing selected
      breakdown: {
        fwb: { adl: 0, iadl: 0, gait: 0, mobility: 0, grip: 0, falls: 0 },
        ct: { coverage: 0, reliability: 0, continuity: 0, coordination: 0 },
        es: {
          fallHazards: 0,
          bathroomSafety: 0,
          kitchenSafety: 0,
          homeLayout: 0,
        },
      },
    };
  }

  const careTeamFields = [
    "ctCoveragePercent",
    "ctDaysPerWeek",
    "ctReliabilityPercent",
    "ctContinuityPercent",
    "ctDocumentationPercent",
    "ctCareplanCurrent",
    "carePlanStatus",
    "ctFamilyCommunication",
    "careDaysPerWeek",
    "totalVisits",
    "visitsBySamePerson",
    "totalScheduledVisits",
    "visitsOnTime",
    "familySatisfaction",
  ];
  const hasCareTeamData =
    data.ctAssessed === true ||
    (data.ctAssessed !== false &&
      careTeamFields.some((field) => {
        const val = data[field];
        return val !== undefined && val !== null && val !== "";
      }));

  // Functional Well Being (FWB)
  const adlItems = FWB_ADL_ITEMS;
  let adlRaw = 0;
  adlItems.forEach((i) => (adlRaw += getIndependenceScore(data[i])));
  const adlPercent = (adlRaw / (adlItems.length * 2)) * 100;
  const fwbAdl = adlPercent * 0.35;

  const iadlItems = [
    "iadlFinances",
    "iadlTransportation",
    "iadlShopping",
    "iadlMeals",
    "iadlCommunication",
    "iadlMedication",
    "iadlHousekeeping",
    "iadlHomeMaintenance",
  ];
  let iadlRaw = 0;
  iadlItems.forEach((i) => (iadlRaw += getIndependenceScore(data[i])));
  const iadlPercent = (iadlRaw / (iadlItems.length * 2)) * 100;
  const fwbIadl = iadlPercent * 0.25;

  // Gait (Weight 10%) - numeric 0-5, missing -> 0
  const gaitScore = getNum(data.gaitSpeed);
  const gaitPercent = (gaitScore / 5) * 100;
  const fwbGait = gaitPercent * 0.1;

  // Mobility (Weight 15%) - numeric 1-4, missing -> 0
  const mobilityScore = getNum(data.mobilityDevice);
  const mobilityPercent = (mobilityScore / 4) * 100;
  const fwbMobility = mobilityPercent * 0.15;

  // Grip (Weight 10%) - numeric 1-4, missing -> 0
  const gripScore = resolveGripStrengthScore(data.gripRight, data.gripLeft);
  const gripPercent = (gripScore / 4) * 100;
  const fwbGrip = gripPercent * 0.1;

  // Falls (Weight 5%) - numeric 0-3, missing -> 0
  const fallsScore = getNum(data.fallsLastYear);
  const fallsPercent = (fallsScore / 3) * 100;
  const fwbFalls = fallsPercent * 0.05;

  let FWB = fwbAdl + fwbIadl + fwbGait + fwbMobility + fwbGrip + fwbFalls;
  FWB = Math.min(100, Math.max(0, FWB)); // cap 0-100

  // Care Team Score (CT) - Excel aligned (4 components: coverage, reliability, continuity, coordination)
  const coveragePercentRaw = getNum(data.ctCoveragePercent);
  const days = getNum(data.ctDaysPerWeek);
  let coverageScore = 0;
  if (hasCareTeamData) {
    if (Number.isFinite(days) && days >= 0 && days <= 7) {
      coverageScore = scoreCareDaysPerWeek(days);
    } else if (!Number.isNaN(coveragePercentRaw) && coveragePercentRaw > 0) {
      coverageScore = Math.min(100, Math.max(0, coveragePercentRaw));
    } else if (days > 7) {
      coverageScore = Math.min(100, Math.max(0, days));
    } else {
      coverageScore = scoreCareDaysPerWeek(0);
    }
  }

  const reliabilityPercent = Math.min(
    100,
    Math.max(0, getNum(data.ctReliabilityPercent)),
  );

  const continuityScore = Math.min(
    100,
    Math.max(0, getNum(data.ctContinuityPercent)),
  );

  // Coordination = average of documentation + communication (0-100)
  const documentationPercent = getNum(data.ctDocumentationPercent);
  const careplanCurrent =
    data.ctCareplanCurrent === true ||
    String(data.carePlanStatus || "").toLowerCase() === "up to date"
      ? 100
      : 0;
  const documentationScore = !Number.isNaN(documentationPercent) &&
    documentationPercent > 0
    ? Math.min(100, Math.max(0, documentationPercent))
    : careplanCurrent;
  const familyCommunication = Math.min(
    100,
    Math.max(0, getNum(data.ctFamilyCommunication)),
  );
  const coordinationScore = (documentationScore + familyCommunication) / 2;

  // Excel-aligned weighted average over 4 components: 35/25/20/20
  const CTraw =
    coverageScore * 0.35 +
    reliabilityPercent * 0.25 +
    continuityScore * 0.2 +
    coordinationScore * 0.2;
  const CT = hasCareTeamData ? Math.min(100, Math.max(0, CTraw)) : 0;

  // Environmental Safety (ES) - backend parity
  const hasHomeSafetyScores = [
    "homeSafetyFallHazards",
    "homeSafetyBathroom",
    "homeSafetyKitchen",
    "homeSafetyLayout",
  ].some((k) => data[k] !== undefined && data[k] !== null && data[k] !== "");

  const hazardKeys = [
    "esLooseRugs",
    "esClutterWalkways",
    "esPoorLighting",
    "esStairsNoRailings",
    "esSlipperyFloors",
    "esNoGrabBars",
    "esUnevenSteps",
    "esCordsOnFloor",
    "esUnevenFlooring",
    "esFootwearNoTraction",
    "esNoNightlights",
    "esPetsUnderfoot",
    "esUnstableFurniture",
    "esItemsStoredImproper",
    "esOtherFallHazard",
    "esUnstableShowerChair",
    "esWetSlipperyFloors",
    "esNoBathroomGrabBars",
    "esSlipperyTub",
    "esNoNonSlipStrips",
    "esLooseBathMats",
    "esLowToiletHeight",
    "esLooseToiletSeat",
    "esHighTubThreshold",
    "esBathroomPoorLighting",
    "esBathroomNoNightlight",
    "esBathroomClutter",
    "esItemsOutOfReach",
    "esTowelBarsForSupport",
    "esCrampedBathroom",
    "esOtherBathroomHazard",
    "esKitchenWetFloors",
    "esGreaseOnFloor",
    "esLooseKitchenMats",
    "esNoNonSlipBacking",
    "esReachingHighCabinets",
    "esUnsafeStepStool",
    "esKitchenClutter",
    "esNoFireExtinguisher",
    "esBendingLowCabinets",
    "esLooseDrawersDoors",
    "esFrayedCords",
    "esKitchenPoorLighting",
    "esApplianceCordsInPath",
    "esKitchenUnevenFlooring",
    "esOverloadedOutlet",
    "esNoSmokeDetector",
    "esNoCO2Detector",
    "esOtherKitchenHazard",
    "esStepsThresholds",
    "esNarrowHallways",
    "esLayoutClutter",
    "esInaccessibleBathroom",
    "esMultiLevelHome",
    "esTightDoorways",
    "esObstructedPathways",
  ];
  const hasEnvironmentData =
    data.environmentAssessed === true ||
    (data.environmentAssessed !== false &&
      (hasHomeSafetyScores || hazardKeys.some((key) => data[key] === true)));

  if (hasEnvironmentData && hasHomeSafetyScores) {
    const clamp0to4 = (val) => Math.max(0, Math.min(4, getNum(val)));
    const clamp0to5 = (val) => Math.max(0, Math.min(5, getNum(val)));
    const fall = clamp0to5(data.homeSafetyFallHazards);
    const bath = clamp0to4(data.homeSafetyBathroom);
    const kitchen = clamp0to4(data.homeSafetyKitchen);
    const layout = clamp0to4(data.homeSafetyLayout);

    const esFall = (fall / 5) * 100 * 0.3;
    const esBath = (bath / 4) * 100 * 0.3;
    const esKitchen = (kitchen / 4) * 100 * 0.2;
    const esLayout = (layout / 4) * 100 * 0.2;

    let ES = esFall + esBath + esKitchen + esLayout;
    ES = Math.min(100, Math.max(0, ES));

    const f = Math.max(0, Math.min(1, FWB / 100));
    const c = Math.max(0, Math.min(1, CT / 100));
    const e = Math.max(0, Math.min(1, ES / 100));

    const CTW = 1 - f; // BF
    const supportIndex = CTW * c; // BH
    const interaction = f * CTW * c; // BI
    const careBoost = 0.4 * supportIndex + 0.6 * interaction; // BG
    const careInfluence = 1 - lambda; // BJ
    const envAdjustment = 1 - gamma * (1 - e); // BK (no clamping)

    const ICS = 100 * f + (100 - 100 * f) * careInfluence * careBoost;
    const kappa = Math.min(0.9, 0.1 + 0.8 * Math.min(c, e));
    const ExtraSubstitution =
      (100 - ICS) * kappa * sigma * supportIndex * careInfluence;
    const ICS_Plus = ICS + ExtraSubstitution;
    const AIP = ICS_Plus * envAdjustment;

    return {
      aipScore: Math.round(AIP),
      wellBeingScore: Math.round(FWB),
      careTeamScore: Math.round(CT),
      environmentScore: Math.round(ES),
      breakdown: {
        fwb: {
          adl: Math.round(fwbAdl),
          iadl: Math.round(fwbIadl),
          gait: Math.round(fwbGait),
          mobility: Math.round(fwbMobility),
          grip: Math.round(fwbGrip),
          falls: Math.round(fwbFalls),
        },
        ct: {
          coverage: Math.round(coverageScore * 0.35),
          reliability: Math.round(reliabilityPercent * 0.25),
          continuity: Math.round(continuityScore * 0.2),
          coordination: Math.round(coordinationScore * 0.2),
        },
        es: {
          fallHazards: Math.round(esFall),
          bathroomSafety: Math.round(esBath),
          kitchenSafety: Math.round(esKitchen),
          homeLayout: Math.round(esLayout),
        },
        calculations: {
          f: f.toFixed(3),
          c: c.toFixed(3),
          e: e.toFixed(3),
          CTW: CTW.toFixed(3),
          supportIndex: supportIndex.toFixed(3),
          interaction: interaction.toFixed(3),
          careBoost: careBoost.toFixed(3),
          careInfluence: careInfluence.toFixed(3),
          kappa: kappa.toFixed(3),
          ICS: ICS.toFixed(2),
          ExtraSubstitution: ExtraSubstitution.toFixed(2),
          ICS_Plus: ICS_Plus.toFixed(2),
          envAdjustment: envAdjustment.toFixed(3),
        },
      },
    };
  }
  if (!hasEnvironmentData) {
    const f = Math.max(0, Math.min(1, FWB / 100));
    const c = Math.max(0, Math.min(1, CT / 100));
    const e = 1; // default 100 when nothing selected
    const CTW = 1 - f;
    const supportIndex = CTW * c;
    const interaction = f * CTW * c;
    const careBoost = 0.4 * supportIndex + 0.6 * interaction;
    const careInfluence = 1 - lambda;
    const envAdjustment = 1 - gamma * (1 - e);
    const ICS = 100 * f + (100 - 100 * f) * careInfluence * careBoost;
    const kappa = Math.min(0.9, 0.1 + 0.8 * Math.min(c, e));
    const ExtraSubstitution =
      (100 - ICS) * kappa * sigma * supportIndex * careInfluence;
    const ICS_Plus = ICS + ExtraSubstitution;
    const AIP = Math.min(100, Math.max(0, ICS_Plus * envAdjustment));
    return {
      aipScore: Math.round(AIP),
      wellBeingScore: Math.round(FWB),
      careTeamScore: Math.round(CT),
      environmentScore: 100, // default when nothing selected
      breakdown: {
        fwb: {
          adl: Math.round(fwbAdl),
          iadl: Math.round(fwbIadl),
          gait: Math.round(fwbGait),
          mobility: Math.round(fwbMobility),
          grip: Math.round(fwbGrip),
          falls: Math.round(fwbFalls),
        },
        ct: {
          coverage: Math.round(coverageScore * 0.35),
          reliability: Math.round(reliabilityPercent * 0.25),
          continuity: Math.round(continuityScore * 0.2),
          coordination: Math.round(coordinationScore * 0.2),
        },
        es: {
          fallHazards: 0,
          bathroomSafety: 0,
          kitchenSafety: 0,
          homeLayout: 0,
        },
      },
    };
  }

  const fallHazardsList = [
    "esLooseRugs",
    "esClutterWalkways",
    "esPoorLighting",
    "esStairsNoRailings",
    "esSlipperyFloors",
    "esNoGrabBars",
    "esUnevenSteps",
    "esCordsOnFloor",
    "esUnevenFlooring",
    "esFootwearNoTraction",
    "esNoNightlights",
    "esPetsUnderfoot",
    "esUnstableFurniture",
    "esItemsStoredImproper",
    "esOtherFallHazard",
  ];
  const fallHazardScore = scoreFallHazardsFromCount(
    countChecked(data, fallHazardsList),
  );
  const esFall = (fallHazardScore / 5) * 100 * 0.3;

  const bathroomHazards = [
    "esUnstableShowerChair",
    "esWetSlipperyFloors",
    "esNoBathroomGrabBars",
    "esSlipperyTub",
    "esNoNonSlipStrips",
    "esLooseBathMats",
    "esLowToiletHeight",
    "esLooseToiletSeat",
    "esHighTubThreshold",
    "esBathroomPoorLighting",
    "esBathroomNoNightlight",
    "esBathroomClutter",
    "esItemsOutOfReach",
    "esTowelBarsForSupport",
    "esCrampedBathroom",
    "esOtherBathroomHazard",
  ];
  const bathroomScore = scoreBathroomFromHazardCount(
    countChecked(data, bathroomHazards),
  );
  const esBath = (bathroomScore / 4) * 100 * 0.3;

  const kitchenHazards = [
    "esKitchenWetFloors",
    "esGreaseOnFloor",
    "esLooseKitchenMats",
    "esNoNonSlipBacking",
    "esReachingHighCabinets",
    "esUnsafeStepStool",
    "esKitchenClutter",
    "esNoFireExtinguisher",
    "esBendingLowCabinets",
    "esLooseDrawersDoors",
    "esFrayedCords",
    "esKitchenPoorLighting",
    "esApplianceCordsInPath",
    "esKitchenUnevenFlooring",
    "esOverloadedOutlet",
    "esNoSmokeDetector",
    "esNoCO2Detector",
    "esOtherKitchenHazard",
  ];
  const kitchenScore = scoreKitchenFromHazardCount(
    countChecked(data, kitchenHazards),
  );
  const esKitchen = (kitchenScore / 4) * 100 * 0.2;

  const layoutBarriersList = [
    "esStepsThresholds",
    "esNarrowHallways",
    "esLayoutClutter",
    "esInaccessibleBathroom",
    "esMultiLevelHome",
    "esTightDoorways",
    "esObstructedPathways",
  ];
  const layoutScore = scoreHomeLayoutFromBarrierCount(
    countChecked(data, layoutBarriersList),
  );
  const esLayout = (layoutScore / 4) * 100 * 0.2;

  let ES = esFall + esBath + esKitchen + esLayout;
  ES = Math.min(100, Math.max(0, ES));

  // Excel-aligned methodology (must match backend)
  const f = Math.max(0, Math.min(1, FWB / 100));
  const c = Math.max(0, Math.min(1, CT / 100));
  const e = Math.max(0, Math.min(1, ES / 100));

  const CTW = 1 - f; // BF
  const supportIndex = CTW * c; // BH
  const interaction = f * CTW * c; // BI
  const careBoost = 0.4 * supportIndex + 0.6 * interaction; // BG
  const careInfluence = 1 - lambda; // BJ
  const envAdjustment = 1 - gamma * (1 - e); // BK (no clamping)

  // ICS = 100*f + (100-100*f) * careInfluence * (0.4*supportIndex + 0.6*interaction)  (BL)
  // Simplified: ICS = 100*f + 100*(1-f) * careInfluence * careBoost
  const ICS = 100 * f + (100 - 100 * f) * careInfluence * careBoost;

  // Kappa (κ) - Excel column BB
  const kappa = Math.min(0.9, 0.1 + 0.8 * Math.min(c, e));

  // ExtraSubstitution - Excel column BM: (100-ICS) * κ * σ * supportIndex * careInfluence
  const ExtraSubstitution =
    (100 - ICS) * kappa * sigma * supportIndex * careInfluence;

  const ICS_Plus = ICS + ExtraSubstitution; // BN
  const AIP = Math.min(100, Math.max(0, ICS_Plus * envAdjustment)); // BO

  return {
    aipScore: Math.round(AIP),
    wellBeingScore: Math.round(FWB),
    careTeamScore: Math.round(CT),
    environmentScore: Math.round(ES),
    breakdown: {
      fwb: {
        adl: Math.round(fwbAdl),
        iadl: Math.round(fwbIadl),
        gait: Math.round(fwbGait),
        mobility: Math.round(fwbMobility),
        grip: Math.round(fwbGrip),
        falls: Math.round(fwbFalls),
      },
      ct: {
        coverage: Math.round(coverageScore * 0.35),
        reliability: Math.round(reliabilityPercent * 0.25),
        continuity: Math.round(continuityScore * 0.2),
        coordination: Math.round(coordinationScore * 0.2),
      },
      es: {
        fallHazards: Math.round(esFall),
        bathroomSafety: Math.round(esBath),
        kitchenSafety: Math.round(esKitchen),
        homeLayout: Math.round(esLayout),
      },
      calculations: {
        f: f.toFixed(3),
        c: c.toFixed(3),
        e: e.toFixed(3),
        CTW: CTW.toFixed(3),
        supportIndex: supportIndex.toFixed(3),
        interaction: interaction.toFixed(3),
        careBoost: careBoost.toFixed(3),
        careInfluence: careInfluence.toFixed(3),
        kappa: kappa.toFixed(3),
        ICS: ICS.toFixed(2),
        ExtraSubstitution: ExtraSubstitution.toFixed(2),
        ICS_Plus: ICS_Plus.toFixed(2),
        envAdjustment: envAdjustment.toFixed(3),
      },
    },
  };
};

export default calculateScoresV2;
